import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { departmentService } from "#/features/departments/api";
import { adminProcApi } from "../../api";
import { ProcError, ProcForm, ProcLoading, ProcSection } from "../../components/ProcurementLayout";
import { useAsyncData, useMutation } from "../../hooks";

export default function FeedbackCreatePage() {
  const navigate = useNavigate();

  const { data: depts, loading: deptsLoading } = useAsyncData(
    () => departmentService.list({ size: 100 }).then((r) => r.content),
    [],
  );

  const [departmentId, setDepartmentId] = useState<number | "">("");
  const [requisitionId, setRequisitionId] = useState<number | "">("");
  const [supplierName, setSupplierName] = useState("");
  const [feedbackType, setFeedbackType] = useState("POSITIVE");
  const [description, setDescription] = useState("");
  const [ratingScore, setRatingScore] = useState(3);
  const [recommendations, setRecommendations] = useState("");

  const { mutate, loading, error } = useMutation(async () => {
    if (!departmentId) throw new Error("Department is required.");
    const result = await adminProcApi.createFeedback({
      departmentId: departmentId as number,
      departmentName: null,
      requisitionId: requisitionId !== "" ? (requisitionId as number) : null,
      requisitionSerialNo: null,
      supplierName,
      feedbackType,
      description,
      ratingScore,
      recommendations: recommendations || null,
    });
    navigate(`/procurement/admin/feedback/${result.id}`);
  });

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <Link to="/procurement/admin/feedback" className="text-sm text-emerald-700 hover:underline">&larr; Back to Feedback</Link>
      </div>

      <ProcForm formNo="PROC-FRM-13" title="Department User Feedback">
        {deptsLoading && <ProcLoading label="Loading departments..." />}
        {error && <ProcError message={error} />}

        <ProcSection title="Feedback Details">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Department *</label>
              <select value={departmentId} onChange={(e) => setDepartmentId(e.target.value ? Number(e.target.value) : "")} className="w-full rounded-xl border px-3 py-2 text-sm" required>
                <option value="">Select department...</option>
                {(depts ?? []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Linked Requisition ID (optional)</label>
              <input type="number" value={requisitionId} onChange={(e) => setRequisitionId(e.target.value ? Number(e.target.value) : "")} className="w-full rounded-xl border px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Supplier Name</label>
              <input type="text" value={supplierName} onChange={(e) => setSupplierName(e.target.value)} className="w-full rounded-xl border px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Feedback Type</label>
              <select value={feedbackType} onChange={(e) => setFeedbackType(e.target.value)} className="w-full rounded-xl border px-3 py-2 text-sm">
                <option value="POSITIVE">POSITIVE</option>
                <option value="NEGATIVE">NEGATIVE</option>
                <option value="NEUTRAL">NEUTRAL</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Rating Score</label>
              <select value={ratingScore} onChange={(e) => setRatingScore(Number(e.target.value))} className="w-full rounded-xl border px-3 py-2 text-sm">
                <option value={1}>1 — Very Poor</option>
                <option value={2}>2 — Poor</option>
                <option value={3}>3 — Fair</option>
                <option value={4}>4 — Good</option>
                <option value={5}>5 — Excellent</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Description</label>
              <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4} className="w-full rounded-xl border px-3 py-2 text-sm" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Recommendations (optional)</label>
              <textarea value={recommendations} onChange={(e) => setRecommendations(e.target.value)} rows={3} className="w-full rounded-xl border px-3 py-2 text-sm" />
            </div>
          </div>
        </ProcSection>

        <div className="flex justify-end gap-3 pt-2">
          <Link to="/procurement/admin/feedback" className="rounded-xl border px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</Link>
          <button type="button" onClick={() => mutate()} disabled={loading} className="rounded-xl bg-emerald-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-800 disabled:opacity-60">
            {loading ? "Saving…" : "Save Feedback"}
          </button>
        </div>
      </ProcForm>
    </div>
  );
}
